import React, { useState } from 'react';
import { DayPicker, Matcher } from 'react-day-picker';
import dayjs from 'dayjs';
import { alpha, Box, ButtonBase, TextField, Typography } from '@mui/material';

import { DayRange, isBackwards } from '../contactList';
import { DatePreset, matchPreset } from './filterModel';

interface DateRangeEditorProps {
  /** Names the typed dates for assistive technology: "<label> from", "<label> to". */
  label: string;
  range: DayRange;
  presets: Array<DatePreset>;
  /** YYYY-MM-DD, the day presets count back from. */
  today: string;
  onChange: (range: DayRange) => void;
  /** Greyed out, such as the app dates while "No app account" is chosen. */
  disabled?: boolean;
  /** A choice past the dates, such as "Never contacted". */
  never?: { label: string; active: boolean; onChange: (active: boolean) => void };
  /** A line under the calendar saying what the date is. */
  hint?: string;
}

const toDate = (day: string) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const toDay = (date: Date) => dayjs(date).format('YYYY-MM-DD');

/** The earliest month the calendar offers: before NationBuilder, so every sign-up fits. */
const START_MONTH = new Date(2000, 0);

const PresetButton = ({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) => (
  <ButtonBase
    onClick={onClick}
    aria-pressed={selected}
    sx={(theme) => ({
      justifyContent: 'flex-start',
      px: 1.25,
      py: 0.75,
      borderRadius: 1.5,
      fontSize: '0.8125rem',
      fontWeight: selected ? 600 : 500,
      whiteSpace: 'nowrap',
      color: selected ? 'primary.dark' : 'text.primary',
      bgcolor: selected ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
      // Chips on a phone, a plain list beside the calendar on a wider screen.
      borderStyle: 'solid',
      borderWidth: { xs: 1, sm: 0 },
      borderColor: selected ? alpha(theme.palette.primary.main, 0.4) : theme.palette.divider,
      '&:hover': { bgcolor: selected ? alpha(theme.palette.primary.main, 0.14) : 'rgba(15, 23, 42, 0.05)' },
      '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' },
    })}
  >
    {label}
  </ButtonBase>
);

/**
 * A date range in whole days, both ends included, either end open: presets on
 * the left, a calendar, and the two dates typed for exactness.
 *
 * The first click on the calendar picks that day; the second makes a range with
 * it, whichever comes first; the next click starts again. Every change applies
 * at once, as the rest of the filters do.
 */
export const DateRangeEditor = ({ label, range, presets, today, onChange, disabled, never, hint }: DateRangeEditorProps) => {
  const [anchor, setAnchor] = useState<string | null>(null);
  const [month, setMonth] = useState<Date>(() => toDate(range.to ?? range.from ?? today));

  const neverOn = Boolean(never?.active);
  const off = disabled || neverOn;
  const backwards = isBackwards(range);
  const preset = matchPreset(range, presets, today);
  const anyTime = !neverOn && !range.from && !range.to;

  const apply = (next: DayRange, show?: string) => {
    setAnchor(null);
    if (never?.active) never.onChange(false);
    onChange(next);
    if (show) setMonth(toDate(show));
  };

  const clickDay = (date: Date) => {
    const day = toDay(date);
    if (anchor === null) {
      setAnchor(day);
      if (never?.active) never.onChange(false);
      onChange({ from: day, to: day });
      return;
    }
    setAnchor(null);
    onChange(day < anchor ? { from: day, to: anchor } : { from: anchor, to: day });
  };

  const setEnd = (end: 'from' | 'to', value: string) => {
    const next = { ...range };
    if (value) next[end] = value;
    else delete next[end];
    apply(next, value || undefined);
  };

  // The chosen days, shaded. An open end runs to today, or back to the calendar's start.
  const from = range.from ? toDate(range.from) : undefined;
  const to = range.to ? toDate(range.to) : undefined;
  const shaded = !off && !backwards && (from || to);
  const todayDate = toDate(today);
  const modifiers: Record<string, Matcher> = shaded
    ? {
        rangeStart: (date: Date) => Boolean(from && dayjs(date).isSame(from, 'day')),
        rangeEnd: (date: Date) => Boolean(to && dayjs(date).isSame(to, 'day')),
        rangeMiddle: (date: Date) =>
          (from ? date > from : true) && (to ? date < to : date <= todayDate) && !(from && dayjs(date).isSame(from, 'day')),
      }
    : {};

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '148px 1fr' }, gap: { xs: 1.5, sm: 2 } }}>
      <Box
        role="group"
        aria-label={`${label}: quick choices`}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'column' },
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: 0.5,
          borderRight: { sm: '1px solid' },
          borderColor: { sm: 'divider' },
          pr: { sm: 1.5 },
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        <PresetButton label="Any time" selected={anyTime} onClick={() => apply({})} />
        {presets.map((choice) => (
          <PresetButton
            key={choice.id}
            label={choice.label}
            selected={!neverOn && preset?.id === choice.id}
            onClick={() => {
              const next = choice.range(today);
              apply(next, next.to ?? next.from);
            }}
          />
        ))}
        {never && (
          <>
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', my: 0.5, display: { xs: 'none', sm: 'block' } }} />
            <PresetButton
              label={never.label}
              selected={neverOn}
              onClick={() => {
                setAnchor(null);
                never.onChange(!neverOn);
              }}
            />
          </>
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={(theme) => ({
            opacity: off ? 0.45 : 1,
            pointerEvents: off ? 'none' : 'auto',
            transition: 'opacity 120ms ease',
            display: 'flex',
            justifyContent: 'center',
            // On .rdp-root itself: the library sets its defaults there, which would hide any set above it.
            '& .rdp-root': {
              '--rdp-accent-color': theme.palette.primary.main,
              '--rdp-accent-background-color': alpha(theme.palette.primary.main, 0.1),
              '--rdp-day-height': '36px',
              '--rdp-day-width': '36px',
              '--rdp-day_button-height': '34px',
              '--rdp-day_button-width': '34px',
              '--rdp-day_button-border-radius': '8px',
              '--rdp-nav-height': '2.25rem',
              '--rdp-nav_button-height': '2rem',
              '--rdp-nav_button-width': '2rem',
              '--rdp-today-color': theme.palette.primary.main,
              '--rdp-range_middle-color': theme.palette.primary.dark,
              '--rdp-weekday-opacity': '0.6',
              fontSize: '0.8125rem',
              m: 0,
            },
            '& .rdp-month_caption': { fontSize: '0.875rem', fontWeight: 600 },
            '& .rdp-dropdowns select': { fontFamily: 'inherit' },
            // Not on the range's ends: their filled button would lose its colour under the white digit.
            '& .rdp-day:not(.rdp-range_start):not(.rdp-range_end) .rdp-day_button:hover:not(:disabled)': {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            },
            '& .rdp-today:not(.rdp-range_start):not(.rdp-range_end) .rdp-day_button': { fontWeight: 700 },
          })}
        >
          <DayPicker
            captionLayout="dropdown"
            startMonth={START_MONTH}
            endMonth={todayDate}
            month={month}
            onMonthChange={setMonth}
            disabled={{ after: todayDate }}
            onDayClick={(date, dayModifiers) => {
              if (!dayModifiers.disabled) clickDay(date);
            }}
            modifiers={modifiers}
            modifiersClassNames={{ rangeStart: 'rdp-range_start', rangeEnd: 'rdp-range_end', rangeMiddle: 'rdp-range_middle' }}
            showOutsideDays
            fixedWeeks
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
          <TextField
            type="date"
            label="From"
            value={range.from ?? ''}
            disabled={off}
            onChange={(event) => setEnd('from', event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': `${label} from`, max: range.to ?? today }}
            error={backwards}
          />
          <TextField
            type="date"
            label="To"
            value={range.to ?? ''}
            disabled={off}
            onChange={(event) => setEnd('to', event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': `${label} to`, min: range.from, max: today }}
            error={backwards}
          />
        </Box>
        {backwards && (
          <Typography variant="caption" color="error" component="p" sx={{ mt: 0.5 }}>
            "To" is before "From", so this date is not applied.
          </Typography>
        )}
        {!backwards && anchor && (
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
            Pick another day to make a range.
          </Typography>
        )}
        {hint && !backwards && !anchor && (
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
